import React from "react";

import {
    ChartsLabelCustomMarkProps,
    ChartsLabelMarkProps,
    ChartsLabelMark as MuiChartsLabelMark
} from "@mui/x-charts/ChartsLabel";
import { Box } from "@mui/material";

import { DeltaSeriesConfig, ChartsLabelProps } from "./types";
import { Text } from "../typography";
import { Row } from "../containers";
import { CHART_AREA_FILL_OPACITY } from "./constants";
import { useAppPalette } from "../../theme";

export function ChartsLabel({ label, markType, markColor, spacing = 0.5 }: ChartsLabelProps) {
    return (
        <Row spacing={spacing}>
            <ChartsLabelMark type={markType} color={markColor} />
            <Text variant="body2" color="caption">
                {label}
            </Text>
        </Row>
    );
}

const MARK_SIZE = 16;

function ChartsLabelMark(props: ChartsLabelMarkProps) {
    if (!props.type || typeof props.type === "string") return <MuiChartsLabelMark {...props} />;
    else {
        const CustomLabelMark = props.type;
        return (
            <Box
                height={MARK_SIZE}
                width={MARK_SIZE}
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <CustomLabelMark {...props} />
            </Box>
        );
    }
}

// Safari collapses a viewBox-only svg to 0x0 inside a flex container
const markSvgProps = { viewBox: `0 0 ${MARK_SIZE} ${MARK_SIZE}`, width: MARK_SIZE, height: MARK_SIZE };

export const getConditionalLabel = (label: string, labelCondensed?: string) => (location: string | undefined) => {
    if (!labelCondensed) return label;
    return location === "tooltip" ? label : labelCondensed;
};

export const getLabelMark = <T extends { date: number }>({
    dash,
    fill,
    deltaFill,
    hideLine
}: {
    dash?: boolean;
    fill?: boolean;
    deltaFill?: DeltaSeriesConfig<T>;
    hideLine?: boolean;
}) => {
    if (hideLine) return HiddenLabelMark;
    if (deltaFill) return createDeltaAreaLabelMark(deltaFill.valueFillColor, deltaFill.referenceFillColor);
    if (dash && fill) return AreaDashLabelMark;
    if (dash) return LineDashLabelMark;
    if (fill) return AreaLabelMark;
    return LineLabelMark;
};

const HiddenLabelMark = ({ className }: ChartsLabelCustomMarkProps) => {
    const { divider } = useAppPalette();
    return <SvgAreaLabelMark className={className} color={divider} />;
};

const LineLabelMark = ({ className, color }: ChartsLabelCustomMarkProps) => (
    <svg {...markSvgProps}>
        <line className={className} x1={2} y1={8} x2={14} y2={8} stroke={color} strokeWidth={2} strokeLinecap="round" />
    </svg>
);

const LineDashLabelMark = ({ className, color }: ChartsLabelCustomMarkProps) => (
    <svg {...markSvgProps}>
        <line
            className={className}
            x1={2}
            y1={8}
            x2={14}
            y2={8}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeDasharray="4, 4"
        />
    </svg>
);

const AreaLabelMark = ({ className, color }: ChartsLabelCustomMarkProps) => (
    <SvgAreaLabelMark className={className} color={color} />
);

const AreaDashLabelMark = ({ className, color }: ChartsLabelCustomMarkProps) => (
    <SvgAreaLabelMark className={className} color={color} dash />
);

const createDeltaAreaLabelMark = (
    colorTop: string,
    colorBottom: string | undefined
): React.ComponentType<ChartsLabelCustomMarkProps> => {
    const Mark = ({ className }: ChartsLabelCustomMarkProps) => {
        return <SvgDeltaAreaLabelMark className={className} colorTop={colorTop} colorBottom={colorBottom} />;
    };
    return Mark;
};

const SvgDeltaAreaLabelMark = ({
    className,
    colorTop,
    colorBottom
}: {
    className?: string;
    colorTop: string;
    colorBottom: string | undefined;
}) => (
    <svg {...markSvgProps}>
        <defs>
            <clipPath id={`${className}-mark-clip`}>
                <rect x={2} y={2} height={12} width={12} rx={2} ry={2} />
            </clipPath>
        </defs>
        <g clipPath={`url(#${className}-mark-clip)`}>
            <polygon
                className={className}
                points={colorBottom ? "2,2 14,2 2,14" : "2,2 14,2 14,14 2,14"}
                fill={colorTop}
                fillOpacity={CHART_AREA_FILL_OPACITY * 2}
            />
            {colorBottom && (
                <polygon
                    className={className}
                    points="14,2 14,14 2,14"
                    fill={colorBottom}
                    fillOpacity={CHART_AREA_FILL_OPACITY * 2}
                />
            )}
        </g>
    </svg>
);

const SvgAreaLabelMark = ({ className, color, dash }: ChartsLabelCustomMarkProps & { dash?: boolean }) => (
    <svg {...markSvgProps}>
        <rect
            className={className}
            x={2}
            y={2}
            height={12}
            width={12}
            rx={2}
            ry={2}
            fill={color}
            fillOpacity={CHART_AREA_FILL_OPACITY * 2}
            stroke={color}
            strokeWidth={1}
            {...(dash && { strokeLinecap: "round", strokeDasharray: "4, 4" })}
        />
    </svg>
);
